import { SpotifyAuth } from './spotify';
import { SimulatedAudioVisualizer } from './visualizerSimulated';
import { SpotifyPlayer } from './player';
import { AudioAnalysisVisualizer } from './audioAnalysis';
import './style.css';

// Load from build-time environment variable
const SPOTIFY_CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
const REDIRECT_URI = window.location.origin;

class App {
  private spotify: SpotifyAuth;
  private visualizer: SimulatedAudioVisualizer;
  private player: SpotifyPlayer | null = null;
  private analysisVisualizer: AudioAnalysisVisualizer;
  private canvas: HTMLCanvasElement;
  private isFullscreen = false;
  private currentTrackId: string | null = null;
  private isPlaying = false;
  private hideUITimeout: number | null = null;
  private hideCursorTimeout: number | null = null;
  private hasTriggeredIntro = false;
  private currentVisualizerType: 'psychedelic' | 'tesla' | 'tunnel' | 'off' | 'particles' = 'tesla';
  private readonly STORAGE_KEY_VISUALIZER = 'spotify-visualizer-selected';

  constructor() {
    this.spotify = new SpotifyAuth({
      clientId: SPOTIFY_CLIENT_ID,
      redirectUri: REDIRECT_URI,
    });

    // Set up 401 handler - redirect to login on token expiration
    this.spotify.setOnUnauthorized(() => {
      console.log('🔒 Token expired - redirecting to login...');
      this.updateStatus('⚠️ Session expired - Please log in again');
      setTimeout(() => {
        this.logout();
      }, 2000);
    });

    this.canvas = document.getElementById('visualizer') as HTMLCanvasElement;
    
    // Load saved visualizer type from localStorage
    this.currentVisualizerType = this.loadVisualizerType();
    
    this.visualizer = new SimulatedAudioVisualizer(this.canvas, this.currentVisualizerType);
    this.analysisVisualizer = new AudioAnalysisVisualizer();

    // Connect analysis to visualizer
    this.analysisVisualizer.setUpdateCallback((data) => {
      this.visualizer.updateAnalysisData(data);
    });

    this.init();
  }

  private async init(): Promise<void> {
    this.setupUI();
    this.setupAutoHide();
    
    // Update button states to reflect loaded visualizer
    this.updateVisualizerButtons();
    
    await this.handleAuthCallback();

    const token = this.spotify.getToken();
    if (token) {
      this.showApp();
      await this.initPlayer(token);
    } else {
      this.showLogin();
    }
  }

  private setupAutoHide(): void {
    // Auto-hide UI and cursor after 3 seconds of inactivity
    const resetTimers = () => {
      // Show UI and cursor
      document.body.classList.remove('hide-ui', 'hide-cursor');
      
      // Clear existing timers
      if (this.hideUITimeout) clearTimeout(this.hideUITimeout);
      if (this.hideCursorTimeout) clearTimeout(this.hideCursorTimeout);
      
      // Hide UI after 3 seconds
      this.hideUITimeout = window.setTimeout(() => {
        document.body.classList.add('hide-ui');
      }, 3000);
      
      // Hide cursor after 3 seconds
      this.hideCursorTimeout = window.setTimeout(() => {
        document.body.classList.add('hide-cursor');
      }, 3000);
    };

    // Listen for mouse movement
    document.addEventListener('mousemove', resetTimers);
    
    // Initial hide after 3 seconds
    this.hideUITimeout = window.setTimeout(() => {
      document.body.classList.add('hide-ui');
    }, 3000);
    
    this.hideCursorTimeout = window.setTimeout(() => {
      document.body.classList.add('hide-cursor');
    }, 3000);
  }

  /**
   * Load saved visualizer type from localStorage
   */
  private loadVisualizerType(): 'psychedelic' | 'tesla' | 'tunnel' | 'off' | 'particles' {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY_VISUALIZER);
      if (saved && ['psychedelic', 'tesla', 'tunnel', 'off', 'particles'].includes(saved)) {
        console.log(`📦 Loaded visualizer from localStorage: ${saved}`);
        return saved as 'psychedelic' | 'tesla' | 'tunnel' | 'off' | 'particles';
      }
    } catch (error) {
      console.warn('Failed to load visualizer from localStorage:', error);
    }
    return 'tesla'; // Default
  }

  /**
   * Save visualizer type to localStorage
   */
  private saveVisualizerType(type: 'psychedelic' | 'tesla' | 'tunnel' | 'off' | 'particles'): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_VISUALIZER, type);
      console.log(`💾 Saved visualizer to localStorage: ${type}`);
    } catch (error) {
      console.warn('Failed to save visualizer to localStorage:', error);
    }
  }

  private async initPlayer(token: string): Promise<void> {
    this.updateStatus('Initializing player...');
    
    this.player = new SpotifyPlayer(
      token,
      () => {
        this.updateStatus('✅ Player ready! Play music from your phone');
        // Start visualizer in idle mode immediately
        this.visualizer.start();
      },
      (state) => {
        this.handlePlayerStateChange(state);
      },
      (error) => {
        this.handlePlayerError(error);
      }
    );

    await this.player.init();
  }

  private setupUI(): void {
    const loginBtn = document.getElementById('login-btn');
    const logoutBtn = document.getElementById('logout-btn');
    const fullscreenBtn = document.getElementById('fullscreen-btn');
    const vizOffBtn = document.getElementById('viz-off');
    const vizPsychedelicBtn = document.getElementById('viz-psychedelic');
    const vizTeslaBtn = document.getElementById('viz-tesla');
    const vizTunnelBtn = document.getElementById('viz-tunnel');
    const vizParticlesBtn = document.getElementById('viz-particles');

    loginBtn?.addEventListener('click', () => this.login());
    logoutBtn?.addEventListener('click', () => this.logout());
    fullscreenBtn?.addEventListener('click', () => this.toggleFullscreen());
    
    vizOffBtn?.addEventListener('click', () => this.switchVisualizer('off'));
    vizPsychedelicBtn?.addEventListener('click', () => this.switchVisualizer('psychedelic'));
    vizTeslaBtn?.addEventListener('click', () => this.switchVisualizer('tesla'));
    vizTunnelBtn?.addEventListener('click', () => this.switchVisualizer('tunnel'));
    vizParticlesBtn?.addEventListener('click', () => this.switchVisualizer('particles'));

    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
      this.updateFullscreenButton();
    });
  }

  private async handleAuthCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    
    if (code) {
      try {
        await this.spotify.handleCallback(code);
        // Remove code from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.reload();
      } catch (error) {
        console.error('Auth error:', error);
        this.updateStatus('Authentication failed');
      }
    }
  }

  private async login(): Promise<void> {
    const authUrl = await this.spotify.getAuthUrl();
    window.location.href = authUrl;
  }

  private logout(): void {
    this.spotify.clearToken();
    
    if (this.player) {
      this.player.disconnect();
      this.player = null;
    }
    
    this.visualizer.stop();
    this.analysisVisualizer.pause();
    this.showLogin();
  }

  private showLogin(): void {
    const loginScreen = document.getElementById('login-screen');
    const appScreen = document.getElementById('app-screen');
    if (loginScreen) loginScreen.style.display = 'flex';
    if (appScreen) appScreen.style.display = 'none';
  }

  private showApp(): void {
    const loginScreen = document.getElementById('login-screen');
    const appScreen = document.getElementById('app-screen');
    if (loginScreen) loginScreen.style.display = 'none';
    if (appScreen) appScreen.style.display = 'block';
  }

  private async toggleFullscreen(): Promise<void> {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  }

  private updateFullscreenButton(): void {
    const btn = document.getElementById('fullscreen-btn');
    if (btn) {
      btn.textContent = this.isFullscreen ? 'Exit Fullscreen' : 'Fullscreen';
    }
  }

  private async switchVisualizer(type: 'psychedelic' | 'tesla' | 'tunnel' | 'off' | 'particles'): Promise<void> {
    // Don't switch if already on this visualizer
    if (type === this.currentVisualizerType) return;

    console.log(`🔄 Switching to ${type} visualizer...`);

    // Stop current visualizer
    this.visualizer.stop();
    this.visualizer.destroy();

    // Create new visualizer
    this.visualizer = new SimulatedAudioVisualizer(this.canvas, type);

    // Start the new visualizer
    this.visualizer.start();

    // Sync state with current playback
    if (this.currentTrackId) {
      // A track is loaded (playing or paused)
      this.visualizer.startIntro();
      
      if (this.isPlaying) {
        // Music is playing - ensure not paused
        this.visualizer.resume();
      } else {
        // Music is paused - pause the visualizer too
        this.visualizer.pause();
      }
      
      // Update track info for Off mode
      if (type === 'off' && this.player) {
        const state = await this.player.getCurrentState();
        if (state?.track_window?.current_track) {
          const track = state.track_window.current_track;
          this.visualizer.setTrackInfo(track.name, track.artists.map((a: any) => a.name).join(', '));
        }
      }
    }

    // Reconnect audio analysis callback
    this.analysisVisualizer.setUpdateCallback((data) => {
      this.visualizer.updateAnalysisData(data);
    });

    // Update current type
    this.currentVisualizerType = type;

    // Save to localStorage
    this.saveVisualizerType(type);

    // Update button states
    this.updateVisualizerButtons();

    // Update status
    const vizName = type === 'psychedelic' ? 'Colors' : type === 'tesla' ? 'Tesla' : type === 'tunnel' ? 'Tunnel' : type === 'particles' ? 'Particles' : 'Off';
    this.updateStatus(`${this.isPlaying ? '▶' : '⏸'} ${vizName} visualizer active`);
  }

  private updateVisualizerButtons(): void {
    const offBtn = document.getElementById('viz-off');
    const psychedelicBtn = document.getElementById('viz-psychedelic');
    const teslaBtn = document.getElementById('viz-tesla');
    const tunnelBtn = document.getElementById('viz-tunnel');
    const particlesBtn = document.getElementById('viz-particles');

    if (offBtn && psychedelicBtn && teslaBtn && tunnelBtn && particlesBtn) {
      offBtn.classList.remove('active');
      psychedelicBtn.classList.remove('active');
      teslaBtn.classList.remove('active');
      tunnelBtn.classList.remove('active');
      particlesBtn.classList.remove('active');
      
      if (this.currentVisualizerType === 'off') {
        offBtn.classList.add('active');
      } else if (this.currentVisualizerType === 'psychedelic') {
        psychedelicBtn.classList.add('active');
      } else if (this.currentVisualizerType === 'tunnel') {
        tunnelBtn.classList.add('active');
      } else if (this.currentVisualizerType === 'particles') {
        particlesBtn.classList.add('active');
      } else {
        teslaBtn.classList.add('active');
      }
    }
  }



  private async handlePlayerStateChange(state: any): Promise<void> {
    if (!state) return;

    const track = state.track_window.current_track;
    const trackId = track.id;
    const wasPlaying = this.isPlaying;
    this.isPlaying = !state.paused;
    const position = state.position / 1000; // Convert to seconds

    // Update UI
    const trackName = document.getElementById('track-name');
    const trackArtist = document.getElementById('track-artist');

    if (trackName) trackName.textContent = track.name;
    if (trackArtist) trackArtist.textContent = track.artists.map((a: any) => a.name).join(', ');

    // Update Off visualizer track info
    if (this.currentVisualizerType === 'off') {
      this.visualizer.setTrackInfo(track.name, track.artists.map((a: any) => a.name).join(', '));
    }

    this.updateStatus(`${this.isPlaying ? '▶ Playing' : '⏸ Paused'}: ${track.name}`);

    // Trigger intro animation when music starts playing for the first time
    if (this.isPlaying && !wasPlaying && !this.hasTriggeredIntro) {
      console.log('🎵 Music started! Triggering intro animation...');
      this.visualizer.startIntro();
      this.hasTriggeredIntro = true;
    }
    
    // Trigger track change effect when track changes (but not on first load)
    if (trackId !== this.currentTrackId && this.currentTrackId !== null && this.isPlaying) {
      console.log('🔄 Track changed! Triggering transition...');
      this.visualizer.startIntro(); // This will create a pulse effect if already running
    }

    // Load analysis data when track changes
    if (trackId !== this.currentTrackId) {
      this.currentTrackId = trackId;
      await this.loadTrackAnalysis(trackId);
    }

    // Update playback position and sync visualizer
    if (this.isPlaying) {
      this.analysisVisualizer.start(position);
      this.visualizer.resume();
    } else {
      this.analysisVisualizer.pause();
      this.visualizer.pause();
    }
  }

  private handlePlayerError = (error: any): void => {
    console.error('Player error:', error);
    
    // Check if token expired
    if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
      this.updateStatus('⚠️ Session expired - Please log in again');
      setTimeout(() => {
        this.logout();
        window.location.reload();
      }, 2000);
    }
  };

  private async loadTrackAnalysis(trackId: string): Promise<void> {
    try {
      const analysis = await this.spotify.getAnalysis(trackId);
      
      if (analysis) {
        this.analysisVisualizer.setAnalysisData(analysis);
        this.updateStatus(`▶ Playing: ${this.currentTrackId ? 'Synced' : 'Ready'}`);
      }
    } catch (error) {
      // Silently continue with fallback
    }
  }

  private updateStatus(message: string): void {
    const status = document.getElementById('status');
    if (status) {
      status.textContent = message;
    }
  }
}

new App();
